import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {
  PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT,
  PRINT_CARD_DEFAULT_MIN_FONT_SIZE_PT,
  PRINT_CARD_DEFAULT_SQUASH_SEPARATOR,
  type PrintCardElement,
  type PrintCardTextElement,
  type PrintCardTextSquashPart,
  type PrintCardTextSquashStyle,
} from "../../print-cards.interface";
import {
  clampPrintCardElementBox,
  PRINT_CARD_MIN_SIZE_CM,
  printCardElementDefaultName,
  printCardTextElementSquashParts,
} from "../../print-card.util";

type PrintCardSquashCanvasSegment =
  | { kind: "part"; part: PrintCardTextSquashPart; text: string }
  | { kind: "sep"; style: PrintCardTextSquashStyle; text: string };

@Component({
  selector: "app-print-card-element-layer",
  templateUrl: "./print-card-element-layer.component.html",
  styleUrls: ["./print-card-element-layer.component.scss"],
  standalone: false,
})
export class PrintCardElementLayerComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input({ required: true }) element!: PrintCardElement;
  @Input() cardSide: "front" | "back" = "front";
  @Input() pageWidthCm = 0;
  @Input() pageHeightCm = 0;
  @Input() safeZoneInsetMm = 0;
  @Input() selected = false;
  @Input() scale = 1;
  @Input() fontFamily = '"Roboto", Arial, sans-serif';
  @Input() fontEpoch = 0;
  @Input() sampleTexts: Partial<Record<string, string>> = {};
  @Output() selectElement = new EventEmitter<string>();
  @Output() contextMenu = new EventEmitter<{
    id: string;
    clientX: number;
    clientY: number;
  }>();
  @Output() interactionStart = new EventEmitter<void>();
  @Output() interactionEnd = new EventEmitter<void>();
  @Output() moveElement = new EventEmitter<{
    id: string;
    xCm: number;
    yCm: number;
    snap?: boolean;
    measure?: boolean;
  }>();
  @Output() resizeElement = new EventEmitter<{
    id: string;
    xCm: number;
    yCm: number;
    widthCm: number;
    heightCm: number;
    handle?: string;
    snap?: boolean;
    measure?: boolean;
  }>();
  @Output() rotateElement = new EventEmitter<{
    id: string;
    rotationDeg: number;
    xCm: number;
    yCm: number;
  }>();

  @ViewChild("textNode") textNodeRef?: ElementRef<HTMLElement>;

  fittedFontSizePt: number | null = null;
  private fitGeneration = 0;
  private dragStart: {
    clientX: number;
    clientY: number;
    xCm: number;
    yCm: number;
  } | null = null;
  private resizeStart: {
    handle: string;
    clientX: number;
    clientY: number;
    xCm: number;
    yCm: number;
    widthCm: number;
    heightCm: number;
  } | null = null;
  private rotateStart: {
    centerX: number;
    centerY: number;
    startPointerAngle: number;
    startRotationDeg: number;
    xCm: number;
    yCm: number;
    widthCm: number;
    heightCm: number;
  } | null = null;
  private dragAxis: "x" | "y" | null = null;
  private lastFitKey = "";
  private fitFrameId = 0;

  get keepSquare(): boolean {
    return this.element.type === "qr" || this.element.type === "nfc";
  }

  get mediaObjectPosition(): string {
    const align =
      this.element.type === "logo" || this.element.type === "image"
        ? this.element.align
        : undefined;
    const valign =
      this.element.type === "logo" || this.element.type === "image"
        ? this.element.valign
        : undefined;
    const objectY =
      valign === "top" ? "top" : valign === "bottom" ? "bottom" : "center";
    if (
      align === "center" ||
      (align == null &&
        this.element.type === "logo" &&
        this.cardSide === "back")
    ) {
      return `center ${objectY}`;
    }
    if (align === "right") {
      return `right ${objectY}`;
    }
    return `left ${objectY}`;
  }

  get textJustifyContent(): string {
    if (this.element.type !== "text") {
      return "flex-start";
    }
    if (this.element.valign === "middle") {
      return "center";
    }
    if (this.element.valign === "bottom") {
      return "flex-end";
    }
    return "flex-start";
  }

  get rotationTransform(): string | null {
    const deg = this.rotationDeg(this.element.rotationDeg ?? 0);
    const parts: string[] = [];
    if (deg !== 0) {
      parts.push(`rotate(${deg}deg)`);
    }
    if (this.element.type !== "shape" && this.element.reversed) {
      parts.push("scaleX(-1)");
    }
    return parts.length > 0 ? parts.join(" ") : null;
  }

  get label(): string {
    return this.element.type;
  }

  get isSquashText(): boolean {
    return (
      this.element.type === "text" &&
      printCardTextElementSquashParts(this.element) != null
    );
  }

  get squashSegments(): PrintCardSquashCanvasSegment[] {
    if (this.element.type !== "text" || !this.isSquashText) {
      return [];
    }
    const parts = printCardTextElementSquashParts(this.element) ?? [];
    const separator =
      typeof this.element.squashSeparator === "string"
        ? this.element.squashSeparator
        : PRINT_CARD_DEFAULT_SQUASH_SEPARATOR;
    const separatorStyle = this.element.squashSeparatorStyle ?? {};
    const segments: PrintCardSquashCanvasSegment[] = [];
    for (let index = 0; index < parts.length; index += 1) {
      const part = parts[index];
      const sample =
        part.variable === "custom" || part.variable === "tagline"
          ? (part.text ?? "").replace(/\r\n/g, "\n").trim()
          : this.sampleTexts[part.variable]?.trim() || "";
      if (index > 0) {
        segments.push({
          kind: "sep",
          style: separatorStyle,
          text: separator,
        });
      }
      segments.push({
        kind: "part",
        part,
        text: sample.length > 0 ? sample : part.variable,
      });
    }
    return segments;
  }

  get textContent(): string {
    const el = this.element;
    if (el.type !== "text") {
      return "";
    }
    if (el.variable === "custom" || el.variable === "tagline") {
      return el.text?.trim() || el.variable;
    }
    if (this.isSquashText) {
      const joined = this.squashSegments
        .map((segment) => segment.text)
        .join("");
      return joined || printCardElementDefaultName(el);
    }
    const variable = el.variable?.trim() || "";
    if (!variable) {
      return "variable";
    }
    return this.sampleTexts[variable]?.trim() || `\${${variable}}`;
  }

  get textLines(): string[] {
    return this.textContent.replace(/\r\n/g, "\n").split("\n");
  }

  get textMaxLines(): number | null {
    if (this.element.type !== "text") {
      return null;
    }
    const n = this.element.maxLines;
    if (typeof n !== "number" || n < 1) {
      return null;
    }
    return Math.min(20, Math.floor(n));
  }

  get textWhiteSpace(): string {
    return this.textMaxLines === 1 ? "nowrap" : "normal";
  }

  get textLineClamp(): number | null {
    const maxLines = this.textMaxLines;
    return maxLines != null && maxLines > 1 ? maxLines : null;
  }

  get textLineHeight(): number {
    if (this.element.type !== "text") {
      return 1;
    }
    if (this.isSquashText) {
      const values = [
        ...(printCardTextElementSquashParts(this.element) ?? []).map(
          (part) => part.lineHeight,
        ),
        this.element.squashSeparatorStyle?.lineHeight,
      ].filter(
        (value): value is number => typeof value === "number" && value >= 0,
      );
      if (values.length > 0) {
        return Math.max(...values);
      }
    }
    return typeof this.element.lineHeight === "number" &&
      this.element.lineHeight >= 0
      ? this.element.lineHeight
      : 1;
  }

  get squashBaseFontSizePt(): number {
    if (this.element.type !== "text" || !this.isSquashText) {
      return PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT;
    }
    const sizes = [
      ...(printCardTextElementSquashParts(this.element) ?? []).map(
        (part) => part.fontSizePt,
      ),
      this.element.squashSeparatorStyle?.fontSizePt,
    ].filter((size): size is number => typeof size === "number" && size > 0);
    if (sizes.length > 0) {
      return Math.max(...sizes);
    }
    return typeof this.element.fontSizePt === "number" &&
      this.element.fontSizePt > 0
      ? this.element.fontSizePt
      : PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT;
  }

  get displayFontSizePt(): number {
    if (this.element.type !== "text") {
      return PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT;
    }
    if (this.element.textFit === "shrink" && this.fittedFontSizePt != null) {
      return this.fittedFontSizePt;
    }
    if (this.isSquashText) {
      return this.squashBaseFontSizePt;
    }
    return typeof this.element.fontSizePt === "number" &&
      this.element.fontSizePt >= 0
      ? this.element.fontSizePt
      : PRINT_CARD_DEFAULT_FONT_SIZE_BODY_PT;
  }

  ngAfterViewInit(): void {
    this.layoutText();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["element"] ||
      changes["scale"] ||
      changes["fontFamily"] ||
      changes["fontEpoch"] ||
      changes["sampleTexts"]
    ) {
      this.layoutText();
    }
  }

  ngOnDestroy(): void {
    this.fitGeneration += 1;
    if (this.fitFrameId) {
      cancelAnimationFrame(this.fitFrameId);
      this.fitFrameId = 0;
    }
  }

  private layoutText(): void {
    if (this.element?.type !== "text" || this.element.textFit !== "shrink") {
      this.fitGeneration += 1;
      if (this.fitFrameId) {
        cancelAnimationFrame(this.fitFrameId);
        this.fitFrameId = 0;
      }
      this.fittedFontSizePt = null;
      this.lastFitKey = "";
      return;
    }
    const textEl: PrintCardTextElement = this.element;
    const squashParts = this.isSquashText
      ? (printCardTextElementSquashParts(textEl) ?? [])
      : [];
    const basePt = this.isSquashText
      ? this.squashBaseFontSizePt
      : typeof textEl.fontSizePt === "number" && textEl.fontSizePt >= 0
        ? textEl.fontSizePt
        : 10;
    const rawMin =
      typeof textEl.minFontSizePt === "number" && textEl.minFontSizePt >= 0
        ? textEl.minFontSizePt
        : Math.min(basePt, PRINT_CARD_DEFAULT_MIN_FONT_SIZE_PT);
    const minPt = Math.min(basePt, Math.max(0, Math.round(rawMin * 10) / 10));
    const squashKey = squashParts
      .map(
        (part) =>
          `${part.variable}:${part.fontSizePt ?? ""}:${part.fontWeight ?? ""}:${part.italic ? 1 : 0}:${part.uppercase ? 1 : 0}:${part.color ?? ""}:${part.lineHeight ?? ""}:${part.text ?? ""}`,
      )
      .join(",");
    const key = [
      textEl.id,
      basePt,
      minPt,
      textEl.maxLines ?? "",
      textEl.widthCm,
      textEl.heightCm,
      this.scale,
      this.textContent,
      this.fontFamily,
      this.fontEpoch,
      textEl.fontWeight ?? "",
      textEl.italic ? 1 : 0,
      textEl.uppercase ? 1 : 0,
      textEl.lineHeight ?? "",
      textEl.squashSeparator ?? "",
      textEl.squashSeparatorStyle?.fontSizePt ?? "",
      textEl.squashSeparatorStyle?.fontWeight ?? "",
      textEl.squashSeparatorStyle?.italic ? 1 : 0,
      textEl.squashSeparatorStyle?.uppercase ? 1 : 0,
      textEl.squashSeparatorStyle?.color ?? "",
      textEl.squashSeparatorStyle?.lineHeight ?? "",
      squashKey,
    ].join("|");
    if (key === this.lastFitKey && this.fittedFontSizePt != null) {
      return;
    }
    const generation = ++this.fitGeneration;
    if (this.fitFrameId) {
      cancelAnimationFrame(this.fitFrameId);
      this.fitFrameId = 0;
    }
    const measure = (retry: number): void => {
      if (generation !== this.fitGeneration) {
        return;
      }
      if (this.element?.type !== "text" || this.element.id !== textEl.id) {
        return;
      }
      if (this.element.textFit !== "shrink") {
        return;
      }
      if (key === this.lastFitKey && this.fittedFontSizePt != null) {
        return;
      }
      const node = this.textNodeRef?.nativeElement;
      if (!node || !(node.clientWidth > 0) || !(node.clientHeight > 0)) {
        if (retry < 8) {
          this.fitFrameId = requestAnimationFrame(() => {
            this.fitFrameId = 0;
            measure(retry + 1);
          });
        }
        return;
      }
      const previousInline = node.style.fontSize;
      const overflows = (pt: number): boolean => {
        node.style.setProperty("font-size", `${pt}pt`, "important");
        node.style.setProperty("overflow", "hidden", "important");
        void node.offsetWidth;
        return (
          node.scrollWidth > node.clientWidth + 0.5 ||
          node.scrollHeight > node.clientHeight + 0.5
        );
      };
      let next = basePt;
      if (overflows(basePt)) {
        if (minPt >= basePt - 0.05 || overflows(minPt)) {
          next = minPt;
        } else {
          let lo = minPt;
          let hi = basePt;
          next = minPt;
          for (let guard = 48; guard > 0 && hi - lo > 0.05; guard -= 1) {
            const mid = Math.round(((lo + hi) / 2) * 1000) / 1000;
            if (mid <= lo || mid >= hi) {
              break;
            }
            if (overflows(mid)) {
              hi = mid;
            } else {
              next = mid;
              lo = mid;
            }
          }
        }
      }
      node.style.removeProperty("overflow");
      if (previousInline) {
        node.style.setProperty("font-size", previousInline);
      } else {
        node.style.removeProperty("font-size");
      }
      if (generation !== this.fitGeneration) {
        return;
      }
      this.lastFitKey = key;
      this.fittedFontSizePt = next;
    };
    const run = (): void => {
      this.fitFrameId = requestAnimationFrame(() => {
        this.fitFrameId = 0;
        measure(0);
      });
    };
    if (document.fonts?.status === "loading" && document.fonts.ready) {
      void document.fonts.ready.then(() => {
        if (generation !== this.fitGeneration) {
          return;
        }
        run();
      });
      return;
    }
    run();
  }

  get mediaSrc(): string | null {
    const el = this.element;
    if (el.type === "image") {
      return el.srcUrl?.trim() || null;
    }
    if (el.type === "logo") {
      const custom = el.srcUrl?.trim() || "";
      if (custom) {
        return custom;
      }
      if (el.srcVariable === "companyImageUrl") {
        return this.sampleTexts["companyImageUrl"]?.trim() || null;
      }
      return null;
    }
    return null;
  }

  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.selectElement.emit(this.element.id);
    this.interactionStart.emit();
    this.dragStart = {
      clientX: event.clientX,
      clientY: event.clientY,
      xCm: this.element.xCm,
      yCm: this.element.yCm,
    };
    const host =
      event.currentTarget instanceof HTMLElement
        ? event.currentTarget
        : (event.target as HTMLElement);
    host.setPointerCapture?.(event.pointerId);
  }

  onContextMenu(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.contextMenu.emit({
      id: this.element.id,
      clientX: event.clientX,
      clientY: event.clientY,
    });
  }

  onResizePointerDown(event: PointerEvent, handle: string): void {
    event.stopPropagation();
    event.preventDefault();
    this.selectElement.emit(this.element.id);
    this.interactionStart.emit();
    const size = this.keepSquare
      ? Math.min(this.element.widthCm, this.element.heightCm)
      : this.element.widthCm;
    this.resizeStart = {
      handle,
      clientX: event.clientX,
      clientY: event.clientY,
      xCm: this.element.xCm,
      yCm: this.element.yCm,
      widthCm: this.keepSquare ? size : this.element.widthCm,
      heightCm: this.keepSquare ? size : this.element.heightCm,
    };
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onRotatePointerDown(event: PointerEvent): void {
    event.stopPropagation();
    event.preventDefault();
    this.selectElement.emit(this.element.id);
    this.interactionStart.emit();
    const host =
      event.currentTarget instanceof Element
        ? event.currentTarget.closest(".pc-layer")
        : null;
    const rect =
      host instanceof HTMLElement ? host.getBoundingClientRect() : null;
    const centerX = rect ? rect.left + rect.width / 2 : event.clientX;
    const centerY = rect ? rect.top + rect.height / 2 : event.clientY;
    const startRotationDeg = this.rotationDeg(this.element.rotationDeg ?? 0);
    this.rotateStart = {
      centerX,
      centerY,
      startPointerAngle: Math.atan2(
        event.clientY - centerY,
        event.clientX - centerX,
      ),
      startRotationDeg,
      xCm: this.element.xCm,
      yCm: this.element.yCm,
      widthCm: this.element.widthCm,
      heightCm: this.element.heightCm,
    };
    (event.target as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  @HostListener("document:pointermove", ["$event"])
  onPointerMove(event: PointerEvent): void {
    const pxPerCm = 37.795275591 * this.scale;
    if (this.rotateStart) {
      const angle = Math.atan2(
        event.clientY - this.rotateStart.centerY,
        event.clientX - this.rotateStart.centerX,
      );
      const deltaDeg =
        ((angle - this.rotateStart.startPointerAngle) * 180) / Math.PI;
      let rotationDeg = this.rotateStart.startRotationDeg + deltaDeg;
      if (event.shiftKey) {
        rotationDeg = Math.round(rotationDeg / 15) * 15;
      }
      rotationDeg = this.rotationDeg(rotationDeg);
      const box = this.fitInPage(
        this.rotateStart.xCm,
        this.rotateStart.yCm,
        this.rotateStart.widthCm,
        this.rotateStart.heightCm,
        this.keepSquare,
        rotationDeg,
      );
      this.rotateElement.emit({
        id: this.element.id,
        rotationDeg,
        xCm: box.xCm,
        yCm: box.yCm,
      });
      return;
    }
    if (this.dragStart) {
      let dx = (event.clientX - this.dragStart.clientX) / pxPerCm;
      let dy = (event.clientY - this.dragStart.clientY) / pxPerCm;
      if (event.shiftKey) {
        if (!this.dragAxis) {
          this.dragAxis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
        }
        if (this.dragAxis === "x") {
          dy = 0;
        } else {
          dx = 0;
        }
      } else {
        this.dragAxis = null;
      }
      const box = this.fitInPage(
        this.dragStart.xCm + dx,
        this.dragStart.yCm + dy,
        this.element.widthCm,
        this.element.heightCm,
      );
      this.moveElement.emit({
        id: this.element.id,
        xCm: box.xCm,
        yCm: box.yCm,
        snap: !event.ctrlKey && !event.metaKey,
        measure: event.altKey,
      });
      return;
    }
    if (!this.resizeStart) {
      return;
    }
    let dx = (event.clientX - this.resizeStart.clientX) / pxPerCm;
    let dy = (event.clientY - this.resizeStart.clientY) / pxPerCm;
    const rotationDeg = this.rotationDeg(this.element.rotationDeg ?? 0);
    if (rotationDeg !== 0) {
      const rad = (rotationDeg * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const localDx = dx * cos + dy * sin;
      const localDy = -dx * sin + dy * cos;
      dx = localDx;
      dy = localDy;
    }
    if (this.element.type !== "shape" && this.element.reversed) {
      dx = -dx;
    }
    let xCm = this.resizeStart.xCm;
    let yCm = this.resizeStart.yCm;
    let widthCm = this.resizeStart.widthCm;
    let heightCm = this.resizeStart.heightCm;
    const h = this.resizeStart.handle;
    if (this.keepSquare) {
      const sized = this.squareResize(h, dx, dy);
      xCm = sized.xCm;
      yCm = sized.yCm;
      widthCm = sized.widthCm;
      heightCm = sized.heightCm;
    } else {
      if (h.includes("e")) {
        widthCm = this.resizeStart.widthCm + dx;
      }
      if (h.includes("s")) {
        heightCm = this.resizeStart.heightCm + dy;
      }
      if (h.includes("w")) {
        widthCm = this.resizeStart.widthCm - dx;
        xCm = this.resizeStart.xCm + dx;
      }
      if (h.includes("n")) {
        heightCm = this.resizeStart.heightCm - dy;
        yCm = this.resizeStart.yCm + dy;
      }
      if (event.shiftKey) {
        const locked = this.lockAspectRatio(h, xCm, yCm, widthCm, heightCm);
        xCm = locked.xCm;
        yCm = locked.yCm;
        widthCm = locked.widthCm;
        heightCm = locked.heightCm;
      }
    }
    if (event.altKey) {
      const centered = this.resizeFromCenter(widthCm, heightCm);
      xCm = centered.xCm;
      yCm = centered.yCm;
      widthCm = centered.widthCm;
      heightCm = centered.heightCm;
    }
    const box = this.fitInPage(xCm, yCm, widthCm, heightCm, this.keepSquare);
    this.resizeElement.emit({
      id: this.element.id,
      xCm: box.xCm,
      yCm: box.yCm,
      widthCm: box.widthCm,
      heightCm: box.heightCm,
      handle: this.resizeStart.handle,
      snap: !event.ctrlKey && !event.metaKey,
      measure: event.altKey,
    });
  }

  @HostListener("document:pointerup")
  onPointerUp(): void {
    if (this.dragStart || this.resizeStart || this.rotateStart) {
      this.interactionEnd.emit();
    }
    this.dragStart = null;
    this.resizeStart = null;
    this.rotateStart = null;
    this.dragAxis = null;
  }

  private squareResize(
    handle: string,
    dx: number,
    dy: number,
  ): { xCm: number; yCm: number; widthCm: number; heightCm: number } {
    const start = this.resizeStart;
    if (!start) {
      return {
        xCm: this.element.xCm,
        yCm: this.element.yCm,
        widthCm: this.element.widthCm,
        heightCm: this.element.heightCm,
      };
    }
    const fromW = handle.includes("e")
      ? start.widthCm + dx
      : handle.includes("w")
        ? start.widthCm - dx
        : null;
    const fromH = handle.includes("s")
      ? start.heightCm + dy
      : handle.includes("n")
        ? start.heightCm - dy
        : null;
    let size = start.widthCm;
    if (fromW !== null && fromH !== null) {
      size =
        Math.abs(fromW - start.widthCm) >= Math.abs(fromH - start.heightCm)
          ? fromW
          : fromH;
    } else if (fromW !== null) {
      size = fromW;
    } else if (fromH !== null) {
      size = fromH;
    }
    size = Math.max(PRINT_CARD_MIN_SIZE_CM, size);
    return {
      xCm: handle.includes("w") ? start.xCm + start.widthCm - size : start.xCm,
      yCm: handle.includes("n") ? start.yCm + start.heightCm - size : start.yCm,
      widthCm: size,
      heightCm: size,
    };
  }

  private lockAspectRatio(
    handle: string,
    xCm: number,
    yCm: number,
    widthCm: number,
    heightCm: number,
  ): { xCm: number; yCm: number; widthCm: number; heightCm: number } {
    const start = this.resizeStart;
    if (!start || start.heightCm <= 0) {
      return { xCm, yCm, widthCm, heightCm };
    }
    const ratio = start.widthCm / start.heightCm;
    const isCorner =
      (handle.includes("n") || handle.includes("s")) &&
      (handle.includes("e") || handle.includes("w"));
    let nextWidth = widthCm;
    let nextHeight = heightCm;
    let nextX = xCm;
    let nextY = yCm;
    if (isCorner) {
      const dw = Math.abs(widthCm - start.widthCm) / start.widthCm;
      const dh = Math.abs(heightCm - start.heightCm) / start.heightCm;
      if (dw >= dh) {
        nextHeight = Math.max(PRINT_CARD_MIN_SIZE_CM, widthCm / ratio);
        nextY = handle.includes("n")
          ? start.yCm + start.heightCm - nextHeight
          : start.yCm;
      } else {
        nextWidth = Math.max(PRINT_CARD_MIN_SIZE_CM, heightCm * ratio);
        nextX = handle.includes("w")
          ? start.xCm + start.widthCm - nextWidth
          : start.xCm;
      }
    } else if (handle.includes("e") || handle.includes("w")) {
      nextHeight = Math.max(PRINT_CARD_MIN_SIZE_CM, widthCm / ratio);
      nextY = start.yCm + (start.heightCm - nextHeight) / 2;
    } else if (handle.includes("n") || handle.includes("s")) {
      nextWidth = Math.max(PRINT_CARD_MIN_SIZE_CM, heightCm * ratio);
      nextX = start.xCm + (start.widthCm - nextWidth) / 2;
    }
    return {
      xCm: nextX,
      yCm: nextY,
      widthCm: Math.max(PRINT_CARD_MIN_SIZE_CM, nextWidth),
      heightCm: Math.max(PRINT_CARD_MIN_SIZE_CM, nextHeight),
    };
  }

  private resizeFromCenter(
    widthCm: number,
    heightCm: number,
  ): { xCm: number; yCm: number; widthCm: number; heightCm: number } {
    const start = this.resizeStart;
    if (!start) {
      return {
        xCm: this.element.xCm,
        yCm: this.element.yCm,
        widthCm,
        heightCm,
      };
    }
    const nextWidth = Math.max(
      PRINT_CARD_MIN_SIZE_CM,
      start.widthCm + (widthCm - start.widthCm) * 2,
    );
    const nextHeight = Math.max(
      PRINT_CARD_MIN_SIZE_CM,
      start.heightCm + (heightCm - start.heightCm) * 2,
    );
    return {
      xCm: start.xCm + start.widthCm / 2 - nextWidth / 2,
      yCm: start.yCm + start.heightCm / 2 - nextHeight / 2,
      widthCm: nextWidth,
      heightCm: nextHeight,
    };
  }

  private fitInPage(
    xCm: number,
    yCm: number,
    widthCm: number,
    heightCm: number,
    square = false,
    rotationDeg = this.element.rotationDeg,
  ): { xCm: number; yCm: number; widthCm: number; heightCm: number } {
    return clampPrintCardElementBox(
      {
        xCm,
        yCm,
        widthCm,
        heightCm,
        rotationDeg,
      },
      {
        widthCm: this.pageWidthCm,
        heightCm: this.pageHeightCm,
        safeZoneInsetMm: this.safeZoneInsetMm,
      },
      square,
    );
  }

  private rotationDeg(deg: number): number {
    if (!Number.isFinite(deg)) {
      return 0;
    }
    let next = deg % 360;
    if (next > 180) {
      next -= 360;
    } else if (next <= -180) {
      next += 360;
    }
    return Math.round(next * 10) / 10;
  }
}
